import React, {forwardRef} from 'react';

import {emptyObject} from '@packages/utilities/fp/utils';
import {useDevAlert} from '@packages/systems/spring-hooks';

import {ModalBackdrop} from './ModalBackdrop';
import {ModalDialog} from './ModalDialog';
import {ModalProvider} from './ModalProvider';

import type {ModalBackdropProps} from './ModalBackdrop';
import type {ModalDialogProps} from './ModalDialog';
import type {ModalProviderProps} from './ModalProvider';
import {ModalActions} from './ModalActions';

export interface ModalProps
  extends ModalProviderProps, ModalBackdropProps, ModalDialogProps {
  hasFullScreenToggle?: boolean;
  isFullScreen?: boolean;
  onFullScreenChange?: (isFullScreen: boolean) => void;
}

/**
 * Modals are dismissable overlays that appear on top of the main content.
 *
 * ```jsx
 * import {Modal} from '@packages/systems/spring';
 * ```
 *
 * ## Usage
 *
 * For most cases, a Modal would be controlled by an `isOpen` state. This is typically bound to an actionable element (e.g. `button`) that triggers the opening of the Modal.
 *
 * ```jsx
 * import {Modal, ModalBody, ModalHeader} from '@packages/systems/spring';
 *
 * const Example = () => {
 *   const [isOpen, setIsOpen] = React.useState(false);
 *   const openModal = () => setIsOpen(true);
 *   const closeModal = () => setIsOpen(false);
 *
 *   return (
 *     <>
 *       <button onClick={openModal}>Open Modal</button>
 *       <Modal isOpen={isOpen} onClose={closeModal}>
 *         <ModalHeader title="Title" />
 *         <ModalBody>
 *           <p>Content...</p>
 *         </ModalBody>
 *       </Modal>
 *     </>
 *   );
 * }
 * ```
 *
 * @component
 */
export const Modal = forwardRef<HTMLDivElement, ModalProps>(
  (
    {
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      children,
      draggableProps,
      elevation = 3,
      onCloseFocusRef,
      hasCloseButton,
      hasFullScreenToggle,
      isFullScreen: isFullScreenProp,
      onFullScreenChange,
      hasSoftCorners = false,
      height,
      id,
      onOpenFocusRef,
      isCentered = false,
      isDraggable = false,
      isDragDisabled,
      isFullHeight = false,
      isNonModalDialog = false,
      isOpen,
      modalSize = 'medium',
      _margin,
      maxHeight,
      maxWidth,
      minHeight,
      minWidth,
      scrollBehavior = 'inside',

      shouldAnchorModalTop,
      shouldDisableAnimations = false,
      shouldHideOnClickOutside = true,
      shouldHideOnEsc = true,
      shouldPreventBodyScroll = true,
      shouldShowBackdrop = true,
      shouldUseBounceAnimation = true,
      onClose,
      onCloseComplete,
      onOpen,
      width,
      style = emptyObject,
      __forceMount,
      ...props
    },
    ref
  ) => {
    useDevAlert(
      isNonModalDialog && !hasCloseButton,
      'Non-modal dialogs should have a close button or some obvious way to dismiss them.'
    );

    const [uncontrolledIsFullScreen, uncontrolledSetIsFullScreen] =
      React.useState(false);
    const isFullScreen = isFullScreenProp ?? uncontrolledIsFullScreen;

    const toggleIsFullScreen = React.useCallback(() => {
      if (isFullScreenProp === undefined) {
        uncontrolledSetIsFullScreen((curr) => !curr);
      }
      onFullScreenChange?.(!isFullScreen);
    }, [
      isFullScreen,
      isFullScreenProp,
      onFullScreenChange,
      uncontrolledSetIsFullScreen,
    ]);

    const providerProps = {
      hasCloseButton,
      isFullScreen,
      isNonModalDialog,
      isOpen,
      onClose,
      onCloseComplete,
      onCloseFocusRef,
      onOpen,
      onOpenFocusRef,
      scrollBehavior,
      shouldAnchorModalTop,
      shouldDisableAnimations,
      shouldHideOnClickOutside,
      shouldHideOnEsc,
      __forceMount,
    };

    const backdropProps = {
      isFullScreen,
      shouldDisableAnimations,
      shouldShowBackdrop,
    };

    const dialogProps = {
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      draggableProps,
      elevation,
      hasSoftCorners,
      height,
      id,
      isCentered,
      isDraggable,
      isDragDisabled,
      isFullHeight,
      isFullScreen,
      _margin,
      maxHeight,
      maxWidth,
      minHeight,
      minWidth,
      modalSize,
      scrollBehavior,
      shouldDisableAnimations,
      shouldPreventBodyScroll,
      shouldUseBounceAnimation,
      style,
      width,
      ...props,
    };

    return (
      <ModalProvider {...providerProps}>
        <ModalBackdrop {...backdropProps}>
          <ModalDialog {...dialogProps} ref={ref}>
            <ModalActions
              hasCloseButton={hasCloseButton}
              hasFullScreenToggle={hasFullScreenToggle}
              isFullScreen={isFullScreen}
              toggleIsFullScreen={toggleIsFullScreen}
            />
            {children}
          </ModalDialog>
        </ModalBackdrop>
      </ModalProvider>
    );
  }
);

Modal.displayName = 'Modal';

export default Modal;
